import { SelectOption as Option } from 'components/select/types';
import { Color } from 'models/colors';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useColorDataService } from 'services/color-data.service';
import { BaseAsyncSelect } from '../../BaseAsyncSelect';

const MIN_REQUIRED_CHARACTERS = 3;

const mapColorToOption = (color: Color): Option<string> => ({
  id: color.colorCode,
  value: color.colorCode,
  label: color.colorCode,
});

export function ColorSelect({
  selectedColors = undefined,
  setSelectedColors = undefined,
  className = undefined,
  showConfirmButton = false,
}: {
  selectedColors?: string[];
  setSelectedColors?: (value: string[]) => void;
  className?: string;
  showConfirmButton?: boolean;
}) {
  const intl = useIntl();
  const { fetchColorDataResponse } = useColorDataService();

  const label = intl.formatMessage({
    id: 'filter.key.selectedColors',
    defaultMessage: 'Colors',
  });

  const placeholder = intl.formatMessage({
    id: 'color.search_placeholder',
    defaultMessage: 'Search colors...',
  });

  const fetchColors = useCallback(
    async (searchTerm: string) => {
      const response = await fetchColorDataResponse(searchTerm);
      return response.data?.colors ?? [];
    },
    [fetchColorDataResponse]
  );

  return (
    <BaseAsyncSelect<Color>
      selectedItems={selectedColors ? selectedColors.map(code => ({ colorCode: code })) : null}
      onSelectionChange={colors => setSelectedColors?.(colors?.map(c => c.colorCode) ?? [])}
      fetchItems={fetchColors}
      getItemId={color => color.colorCode}
      itemToOption={mapColorToOption}
      minSearchCharacters={MIN_REQUIRED_CHARACTERS}
      label={label}
      placeholder={placeholder}
      showConfirmButton={showConfirmButton}
      id="color-filter"
      className={className}
    />
  );
}
